import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Avatar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Chip,
  Divider,
} from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getCurrentUser, updateUser, getUserProfile, changePassword } from '../services/users';
import { User } from '../types';
import { useAppContext } from '../contexts/AppContext';
import { useLang } from '../contexts/langContext';
import { useBranding } from '../contexts/BrandingContext';
import { VersionDisplay } from '../components/common/VersionDisplay';
import { getStrongPasswordErrorMessage, isStrongPassword } from '../utils/password';
import ApiKeyManager from '../components/profile/ApiKeyManager';

function UserProfilePage() {
  const { lang, toggleLanguage } = useLang();
  const { requireStrongPassword } = useBranding();
  const { user, setUser } = useAuth();
  const { userId } = useParams<{ userId?: string }>();
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [defaultSpeakerProfile, setDefaultSpeakerProfile] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const { setAppTitle } = useAppContext();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');

  console.log('users:', user);
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (userId && userId !== user?._id) {
          const userData = await getUserProfile(userId);
          setProfileUser(userData);
          setName(userData.name);
          setEmail(userData.email);
          setDefaultSpeakerProfile(userData.defaultSpeakerProfile || '');
        } else {
          const userData = await getCurrentUser();
          setUser(userData);
          setName(userData.name);
          setEmail(userData.email);
          setDefaultSpeakerProfile(userData.defaultSpeakerProfile || '');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [userId, user?._id]);

  useEffect(() => {
    setAppTitle(lang === 'en' ? 'User Profile' : '用户资料');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const updatedUser = await updateUser({ name, email, defaultSpeakerProfile });
      setUser(updatedUser);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangePasswordError('');
    if (newPassword !== confirmNewPassword) {
      setChangePasswordError(lang === 'en' ? 'Passwords do not match' : '密码不匹配');
      return;
    }

    if (requireStrongPassword && !isStrongPassword(newPassword)) {
      setChangePasswordError(getStrongPasswordErrorMessage(lang));
      return;
    }

    try {
      await changePassword(currentPassword, newPassword);
      alert(lang === 'en' ? 'Password changed successfully' : '密码更改成功');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      setChangePasswordError(lang === 'en' ? 'Error changing password' : '更改密码时出错');
    }
  };

  const handleToggleLanguage = () => {
    toggleLanguage();
  };

  const isOwnProfile = !userId || userId === user?._id;

  return (
    <Container sx={{ p: 3 }} maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar sx={{ width: 100, height: 100, fontSize: 40 }}>{name.charAt(0).toUpperCase()}</Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" gutterBottom>
              {isOwnProfile ? (lang === 'en' ? 'My Profile' : '我的资料') : `${name}'s Profile`}
            </Typography>
            {user?.role === 'Admin' && <Chip label={lang === 'en' ? 'Administrator' : '管理员'} color="primary" size="small" />}
          </Grid>
        </Grid>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {lang === 'en' ? 'Profile Information' : '个人信息'}
          </Typography>
          <TextField
            margin="normal"
            fullWidth
            label={lang === 'en' ? 'Name' : '姓名'}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!isEditing || !isOwnProfile}
          />
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {lang === 'en' ? 'Email' : '邮箱'}
            </Typography>
            <Typography variant="body1">{email}</Typography>
          </Box>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {lang === 'en' ? 'Authentication Type' : '认证类型'}
            </Typography>
            <Typography variant="body1">
              {!user?.authType || user?.authType === 'local'
                ? lang === 'en'
                  ? 'Local Account'
                  : '本地账户'
                : user?.authType === 'seeyon_chat'
                ? lang === 'en'
                  ? 'Seeyon Chat'
                  : '致慧泉'
                : user?.authType === 'oauth'
                ? lang === 'en'
                  ? 'OAuth'
                  : 'OAuth认证'
                : user?.authType === 'unsafe_auth'
                ? lang === 'en'
                  ? 'Proxy Auth'
                  : '代理认证'
                : user?.authType || (lang === 'en' ? 'Unknown' : '未知')}
            </Typography>
          </Box>
          <TextField
            margin="normal"
            fullWidth
            multiline
            rows={2}
            placeholder={
              lang === 'en'
                ? 'Set your default speaker profile which will be used for presentations, e.g. Infinite Innovation Company Admin Manager Li Ming'
                : '设置演示文稿的默认演讲者介绍，例如：无限创新公司行政部主管李明'
            }
            label={lang === 'en' ? 'Default Speaker Profile' : '默认演讲者介绍'}
            value={defaultSpeakerProfile}
            onChange={(e) => setDefaultSpeakerProfile(e.target.value)}
            disabled={!isEditing || !isOwnProfile}
            helperText={
              lang === 'en'
                ? 'Set your default speaker profile which will be used for presentations'
                : '设置默认演讲者介绍，将用于演示文稿的生成的内容'
            }
          />
          {isOwnProfile &&
            (isEditing ? (
              <Box sx={{ mt: 2 }}>
                <Button type="submit" variant="contained" sx={{ mr: 1 }}>
                  {lang === 'en' ? 'Save Changes' : '保存更改'}
                </Button>
                <Button onClick={() => setIsEditing(false)} variant="outlined">
                  {lang === 'en' ? 'Cancel' : '取消'}
                </Button>
              </Box>
            ) : (
              <Button onClick={() => setIsEditing(true)} variant="contained" sx={{ mt: 2 }}>
                {lang === 'en' ? 'Edit Profile' : '编辑资料'}
              </Button>
            ))}
        </Box>
        {isOwnProfile && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              {lang === 'en' ? 'Account Settings' : '账户设置'}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              sx={{ my: 1 }}
              onClick={() => setShowChangePassword(true)}
              disabled={user?.authType !== 'local' && user?.authType !== undefined && user?.authType !== null}
            >
              {lang === 'en' ? 'Change Password' : '更改密码'}
            </Button>
            {user?.authType !== 'local' && user?.authType !== undefined && user?.authType !== null && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {lang === 'en'
                  ? 'Password changes are only available for local accounts. Your account uses external authentication.'
                  : '只有本地账户可以更改密码。您的账户使用外部认证。'}
              </Typography>
            )}
            <ApiKeyManager lang={lang} />
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              {lang === 'en' ? 'Language Settings' : '语言设置'}
            </Typography>
            <Button variant="outlined" color="primary" sx={{ mt: 1 }} onClick={handleToggleLanguage} startIcon={<LanguageIcon />}>
              Switch Language / 切换语言
            </Button>
            <Divider sx={{ my: 3 }} />
            <VersionDisplay showDetails={true} />
            <Dialog open={showChangePassword} onClose={() => setShowChangePassword(false)}>
              <DialogTitle>{lang === 'en' ? 'Change Password' : '更改密码'}</DialogTitle>
              <DialogContent>
                <Box component="form" onSubmit={handleChangePassword} noValidate sx={{ mt: 3 }}>
                  <TextField
                    margin="normal"
                    fullWidth
                    label={lang === 'en' ? 'Current Password' : '当前密码'}
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                  <TextField
                    margin="normal"
                    fullWidth
                    label={lang === 'en' ? 'New Password' : '新密码'}
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    inputProps={{ minLength: requireStrongPassword ? 8 : 6 }}
                  />
                  <TextField
                    margin="normal"
                    fullWidth
                    label={lang === 'en' ? 'Confirm New Password' : '确认新密码'}
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    inputProps={{ minLength: requireStrongPassword ? 8 : 6 }}
                  />
                  {changePasswordError && (
                    <Typography color="error" sx={{ mt: 2 }}>
                      {changePasswordError}
                    </Typography>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowChangePassword(false)}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
                <Button type="submit" variant="contained" onClick={handleChangePassword}>
                  {lang === 'en' ? 'Submit' : '提交'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default UserProfilePage;
